import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  HardDrive, FolderOpen, FileText, RefreshCw, Link2, Unlink, ChevronRight,
  Loader2, ArrowLeft, Plus, Trash2, CheckCircle, Key, Shield,
  Upload, Inbox, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Reusable folder browser dialog ────────────────────

function FolderBrowserDialog({ open, onOpenChange, provider, headers, mappedFolders, onFolderMapped, departments }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stack, setStack] = useState([]);
  const [isDeptOpen, setIsDeptOpen] = useState(false);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [folderToMap, setFolderToMap] = useState(null);

  const isDropbox = provider === 'dropbox';
  const rootItem = isDropbox ? { path: '', name: 'Dropbox', id: 'root' } : { id: 'root', name: 'Drive', path: '' };

  useEffect(() => {
    if (open) {
      setStack([rootItem]);
      loadFolders(isDropbox ? '' : 'root');
    }
  }, [open]);

  const loadFolders = async (parentId) => {
    setLoading(true);
    try {
      const params = isDropbox ? { parent_path: parentId } : { parent_id: parentId };
      const { data } = await axios.get(`${API}/${provider}/folders`, { headers, params });
      setFolders(data.folders || []);
    } catch { setFolders([]); }
    finally { setLoading(false); }
  };

  const navigate = (folder) => {
    setStack(prev => [...prev, folder]);
    loadFolders(isDropbox ? folder.path : folder.id);
  };

  const goBack = () => {
    if (stack.length <= 1) return;
    const newStack = stack.slice(0, -1);
    setStack(newStack);
    const parent = newStack[newStack.length - 1];
    loadFolders(isDropbox ? parent.path : parent.id);
  };

  const confirmMap = (folder) => {
    setFolderToMap(folder);
    setSelectedDepts([]);
    setIsDeptOpen(true);
  };

  const handleMap = async () => {
    if (!folderToMap) return;
    try {
      const body = isDropbox
        ? { folder_path: folderToMap.path, folder_name: folderToMap.name, department_ids: selectedDepts }
        : { folder_id: folderToMap.id, folder_name: folderToMap.name, department_ids: selectedDepts };
      await axios.post(`${API}/${provider}/mapped-folders`, body, { headers });
      toast.success(`Dossier "${folderToMap.name}" mappe`);
      setIsDeptOpen(false);
      onOpenChange(false);
      onFolderMapped();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const isMapped = (f) => {
    if (isDropbox) return mappedFolders.some(m => m.folder_path === f.path);
    return mappedFolders.some(m => m.folder_id === f.id);
  };

  return (
    <>
      <Dialog open={open && !isDeptOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm">Parcourir {isDropbox ? 'Dropbox' : 'Google Drive'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap">
              {stack.map((item, i) => (
                <span key={i} className="flex items-center gap-0.5">
                  {i > 0 && <ChevronRight className="w-3 h-3" />}
                  <button onClick={() => { setStack(stack.slice(0, i + 1)); loadFolders(isDropbox ? item.path : item.id); }} className="hover:text-foreground">{item.name}</button>
                </span>
              ))}
            </div>
            {stack.length > 1 && <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={goBack}><ArrowLeft className="w-3 h-3" /> Retour</Button>}
            <ScrollArea className="h-64 border rounded-lg">
              {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
                : folders.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">Aucun sous-dossier</p>
                : <div className="p-1">{folders.map(f => (
                    <div key={f.id} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded text-sm">
                      <button onClick={() => navigate(f)} className="flex items-center gap-2 min-w-0 text-left flex-1">
                        <FolderOpen className="w-4 h-4 text-blue-500 shrink-0" /><span className="truncate">{f.name}</span>
                      </button>
                      {isMapped(f) ? <Badge variant="outline" className="text-[9px] gap-1 text-emerald-600"><CheckCircle className="w-3 h-3" /> Mappe</Badge>
                        : <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => confirmMap(f)}><Plus className="w-3 h-3 mr-1" /> Mapper</Button>}
                    </div>
                  ))}</div>}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeptOpen} onOpenChange={setIsDeptOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Associer "{folderToMap?.name}" a des departements</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {departments.length === 0 ? <p className="text-sm text-muted-foreground">Aucun departement configure</p>
              : departments.map(d => (
                <label key={d.id} className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-muted/50">
                  <input type="checkbox" checked={selectedDepts.includes(d.id)} onChange={(e) => setSelectedDepts(prev => e.target.checked ? [...prev, d.id] : prev.filter(id => id !== d.id))} className="rounded" />
                  <span className="text-sm">{d.name}</span>
                </label>
              ))}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setIsDeptOpen(false)}>Annuler</Button>
            <Button size="sm" onClick={handleMap}>Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Google Drive Connector ────────────────────

function GoogleDriveConnector({ token, isAdmin }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mappedFolders, setMappedFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [saJsonText, setSaJsonText] = useState('');
  const [sharedDriveId, setSharedDriveId] = useState('');
  const [connecting, setConnecting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, f, d, dept] = await Promise.all([
        axios.get(`${API}/drive/status`, { headers }),
        axios.get(`${API}/drive/mapped-folders`, { headers }).catch(() => ({ data: { mappings: [] } })),
        axios.get(`${API}/drive/documents`, { headers }).catch(() => ({ data: { documents: [] } })),
        axios.get(`${API}/departments`, { headers }).catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setMappedFolders(f.data.mappings || []);
      setDocuments(d.data.documents || []);
      setDepartments(dept.data.departments || dept.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, []);

  const handleConnectOAuth = async () => {
    try { const { data } = await axios.get(`${API}/drive/connect`, { headers }); window.location.href = data.authorization_url; }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleConnectSA = async () => {
    let parsed;
    try { parsed = JSON.parse(saJsonText); } catch { toast.error('JSON invalide'); return; }
    setConnecting(true);
    try {
      await axios.post(`${API}/drive/connect-service-account`, { service_account_json: parsed, shared_drive_id: sharedDriveId.trim() }, { headers });
      toast.success('Google Drive connecte via Service Account !');
      setSaJsonText(''); setSharedDriveId('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    try { await axios.delete(`${API}/drive/disconnect`, { headers }); setStatus({ connected: false }); setMappedFolders([]); setDocuments([]); toast.success('Google Drive deconnecte'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try { const { data } = await axios.post(`${API}/drive/sync`, {}, { headers }); toast.success(`${data.synced} document(s) synchronise(s)`); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSyncing(false); }
  };

  const handleUnmap = async (folderId) => {
    try { await axios.delete(`${API}/drive/mapped-folders/${folderId}`, { headers }); toast.success('Mapping supprime'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <Card><CardContent className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></CardContent></Card>;

  return (
    <>
      <Card data-testid="drive-connector-card">
        <CardHeader className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <HardDrive className="w-4 h-4" /> Google Drive
              {status?.connected && <Badge variant="outline" className="text-[9px] text-emerald-600 gap-1"><CheckCircle className="w-3 h-3" /> Connecte</Badge>}
              {status?.connection_type === 'service_account' && <Badge variant="secondary" className="text-[9px] gap-1"><Key className="w-3 h-3" /> SA</Badge>}
            </CardTitle>
            <div className="flex items-center gap-2">
              {status?.connected && <span className="text-[10px] text-muted-foreground">{mappedFolders.length} dossier(s) · {documents.length} doc(s)</span>}
              {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>
          </div>
        </CardHeader>

        {expanded && (
          <CardContent className="space-y-4 pt-0">
            {status?.connected ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <p>Connecte depuis {status.connected_at ? new Date(status.connected_at).toLocaleDateString('fr-FR') : '-'}</p>
                    {status.service_account_email && <p>{status.service_account_email}</p>}
                  </div>
                  {isAdmin && <Button variant="outline" size="sm" className="text-destructive gap-1 text-xs" onClick={handleDisconnect}><Unlink className="w-3.5 h-3.5" /> Deconnecter</Button>}
                </div>

                {/* Mapped folders */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">Dossiers mappes</span>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="h-7 gap-1 text-[10px]" onClick={() => setBrowseOpen(true)}><Plus className="w-3 h-3" /> Ajouter</Button>
                        <Button variant="outline" size="sm" className="h-7 gap-1 text-[10px]" onClick={handleSync} disabled={syncing || mappedFolders.length === 0}>
                          <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> Sync
                        </Button>
                      </div>
                    )}
                  </div>
                  {mappedFolders.length === 0 ? <p className="text-[10px] text-muted-foreground">Aucun dossier mappe</p>
                    : mappedFolders.map(m => (
                      <div key={m.folder_id || m.id} className="flex items-center justify-between p-2 rounded-lg border text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <FolderOpen className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          <span className="truncate">{m.folder_name}</span>
                          {m.department_names?.map(d => <Badge key={d} variant="secondary" className="text-[8px] px-1 py-0">{d}</Badge>)}
                        </div>
                        {isAdmin && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleUnmap(m.folder_id || m.id)}><Trash2 className="w-3 h-3 text-destructive" /></Button>}
                      </div>
                    ))}
                </div>

                {/* Documents count */}
                {documents.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <FileText className="w-3.5 h-3.5" /> {documents.length} document(s) indexe(s)
                  </div>
                )}
              </>
            ) : (
              <Tabs defaultValue="service_account" className="w-full">
                <TabsList className="w-full">
                  <TabsTrigger value="service_account" className="flex-1 gap-1 text-xs"><Key className="w-3 h-3" /> Service Account</TabsTrigger>
                  <TabsTrigger value="oauth" className="flex-1 gap-1 text-xs"><Shield className="w-3 h-3" /> OAuth</TabsTrigger>
                </TabsList>
                <TabsContent value="service_account" className="space-y-3 pt-2">
                  <div className="space-y-2">
                    <Label className="text-xs">Fichier JSON du Service Account</Label>
                    <Input type="file" accept=".json" onChange={(e) => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = (ev) => setSaJsonText(ev.target.result); r.readAsText(f); } }} className="cursor-pointer" data-testid="sa-file-input" />
                    {saJsonText && <p className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> JSON charge</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">ID Drive partage <span className="text-muted-foreground font-normal">(optionnel)</span></Label>
                    <Input placeholder="ex: 0AJ_xxx" value={sharedDriveId} onChange={(e) => setSharedDriveId(e.target.value)} data-testid="shared-drive-id-input" />
                  </div>
                  <Button className="w-full gap-1 text-xs" onClick={handleConnectSA} disabled={!saJsonText || connecting} data-testid="connect-sa-btn">
                    {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Connecter
                  </Button>
                </TabsContent>
                <TabsContent value="oauth" className="space-y-3 pt-2">
                  <p className="text-xs text-muted-foreground">Autorisez l'acces via votre compte Google</p>
                  <Button className="w-full gap-1 text-xs" onClick={handleConnectOAuth} data-testid="connect-oauth-btn"><Link2 className="w-3.5 h-3.5" /> Connecter via OAuth</Button>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        )}
      </Card>

      <FolderBrowserDialog
        open={browseOpen} onOpenChange={setBrowseOpen}
        provider="drive" headers={headers} mappedFolders={mappedFolders}
        onFolderMapped={fetchAll} departments={departments}
      />
    </>
  );
}

// ─── Dropbox Connector ────────────────────

function DropboxConnector({ token, isAdmin }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mappedFolders, setMappedFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, f, d, dept] = await Promise.all([
        axios.get(`${API}/dropbox/status`, { headers }),
        axios.get(`${API}/dropbox/mapped-folders`, { headers }).catch(() => ({ data: { mappings: [] } })),
        axios.get(`${API}/dropbox/documents`, { headers }).catch(() => ({ data: { documents: [] } })),
        axios.get(`${API}/departments`, { headers }).catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setMappedFolders(f.data.mappings || []);
      setDocuments(d.data.documents || []);
      setDepartments(dept.data.departments || dept.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, []);

  const handleConnect = async () => {
    try { const { data } = await axios.get(`${API}/dropbox/connect`, { headers }); window.location.href = data.authorization_url; }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDisconnect = async () => {
    try { await axios.delete(`${API}/dropbox/disconnect`, { headers }); setStatus({ connected: false }); setMappedFolders([]); setDocuments([]); toast.success('Dropbox deconnecte'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try { const { data } = await axios.post(`${API}/dropbox/sync`, {}, { headers }); toast.success(`${data.synced} document(s) synchronise(s)`); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSyncing(false); }
  };

  const handleUnmap = async (path) => {
    try { await axios.delete(`${API}/dropbox/mapped-folders${path}`, { headers }); toast.success('Mapping supprime'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <Card><CardContent className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></CardContent></Card>;

  return (
    <>
      <Card data-testid="dropbox-connector-card">
        <CardHeader className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Inbox className="w-4 h-4" /> Dropbox
              {status?.connected && <Badge variant="outline" className="text-[9px] text-emerald-600 gap-1"><CheckCircle className="w-3 h-3" /> Connecte</Badge>}
            </CardTitle>
            <div className="flex items-center gap-2">
              {status?.connected && <span className="text-[10px] text-muted-foreground">{mappedFolders.length} dossier(s) · {documents.length} doc(s)</span>}
              {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>
          </div>
        </CardHeader>

        {expanded && (
          <CardContent className="space-y-4 pt-0">
            {status?.connected ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <p>Connecte depuis {status.connected_at ? new Date(status.connected_at).toLocaleDateString('fr-FR') : '-'}</p>
                    {status.account_email && <p>{status.account_email}</p>}
                  </div>
                  {isAdmin && <Button variant="outline" size="sm" className="text-destructive gap-1 text-xs" onClick={handleDisconnect}><Unlink className="w-3.5 h-3.5" /> Deconnecter</Button>}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">Dossiers mappes</span>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="h-7 gap-1 text-[10px]" onClick={() => setBrowseOpen(true)}><Plus className="w-3 h-3" /> Ajouter</Button>
                        <Button variant="outline" size="sm" className="h-7 gap-1 text-[10px]" onClick={handleSync} disabled={syncing || mappedFolders.length === 0}>
                          <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> Sync
                        </Button>
                      </div>
                    )}
                  </div>
                  {mappedFolders.length === 0 ? <p className="text-[10px] text-muted-foreground">Aucun dossier mappe</p>
                    : mappedFolders.map(m => (
                      <div key={m.folder_path || m.id} className="flex items-center justify-between p-2 rounded-lg border text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <FolderOpen className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          <span className="truncate">{m.folder_name}</span>
                          {m.department_names?.map(d => <Badge key={d} variant="secondary" className="text-[8px] px-1 py-0">{d}</Badge>)}
                        </div>
                        {isAdmin && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleUnmap(m.folder_path)}><Trash2 className="w-3 h-3 text-destructive" /></Button>}
                      </div>
                    ))}
                </div>

                {documents.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <FileText className="w-3.5 h-3.5" /> {documents.length} document(s) indexe(s)
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Autorisez l'acces a votre Dropbox</p>
                <Button className="gap-1 text-xs" onClick={handleConnect} data-testid="connect-dropbox-btn"><Link2 className="w-3.5 h-3.5" /> Connecter</Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      <FolderBrowserDialog
        open={browseOpen} onOpenChange={setBrowseOpen}
        provider="dropbox" headers={headers} mappedFolders={mappedFolders}
        onFolderMapped={fetchAll} departments={departments}
      />
    </>
  );
}

// ─── Main Documents Section ────────────────────

export function DocumentsSection({ token, user }) {
  const isAdmin = ['owner', 'admin'].includes(user?.role);

  return (
    <div className="space-y-4" data-testid="documents-section">
      <div className="space-y-1">
        <h3 className="text-sm font-medium">Sources documentaires</h3>
        <p className="text-xs text-muted-foreground">Connectez vos espaces de stockage pour enrichir le contexte RAG de vos clones</p>
      </div>
      <GoogleDriveConnector token={token} isAdmin={isAdmin} />
      <DropboxConnector token={token} isAdmin={isAdmin} />
    </div>
  );
}
